import { del, get, post, put } from 'core/network/RestApi';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';
import { TFunction } from 'i18next';
import { ITemplate, ITemplateLanguageStatus } from 'entities/template/template';
import {
  IAddTemplateLanguagePayload,
  OnsiteTemplateContainer,
  TemplateContainer,
} from 'sections/design/template-management/entities/templates';
import { ISubjectLine } from 'entities/subjectLine/subjectLine';
import { generatePath } from 'react-router';
import { AccessLevel } from 'entities/user';
import { TAB_TO_TEMPLATE_TYPE } from '../constants/tabs';
import { TemplateTypes } from '../constants/templateTypes';

interface TemplatesRequest {
  siteId: string;
  t: TFunction;
  accessLevel?: AccessLevel;
}

const toTemplateArray = (data: ITemplate[] | null | undefined): ITemplate[] => {
  if (data == null) {
    return [];
  }
  return Array.isArray(data) ? data : [];
};

export const getTemplates = async ({ siteId, t }: TemplatesRequest): Promise<ITemplate[]> => {
  try {
    const result = await get<ITemplate[]>(generatePath(TemplateApiEndpoints.GET_TEMPLATES, { siteId }));
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getArchivedTemplates = async ({
  siteId,
  channel,
  templateType,
  t,
}: TemplatesRequest & { channel: string; templateType?: string }): Promise<ITemplate[]> => {
  try {
    // For onsite and MPS templates, fetch from regular endpoints and filter by isArchived
    if (channel === 'onsite' || channel === 'mps') {
      let templates: ITemplate[] = [];

      const backendTemplateType = templateType ? TAB_TO_TEMPLATE_TYPE[templateType] || templateType : undefined;

      switch (backendTemplateType) {
        case 'mpsEmbed':
          templates = await getMPSEmbedTemplates({ siteId, t });
          break;
        case 'mpsPopup':
          templates = await getMPSPopupTemplates({ siteId, t });
          break;
        case 'emailAcquisition':
        case 'dataAcquisition':
          templates = await getEmailAcquisitions({ siteId, t });
          break;
        case 'sendMyBasket':
          templates = await getSendMyBasketOnsiteTemplates({ siteId, t });
          break;
        case 'displayOnly':
          templates = await getDisplayOnlyTemplates({ siteId, t });
          break;
        default:
          const [emailAcquisition, sendMyBasket, displayOnly, mpsEmbed, mpsPopup] = await Promise.all([
            getEmailAcquisitions({ siteId, t }),
            getSendMyBasketOnsiteTemplates({ siteId, t }),
            getDisplayOnlyTemplates({ siteId, t }),
            getMPSEmbedTemplates({ siteId, t }),
            getMPSPopupTemplates({ siteId, t }),
          ]);
          templates = [...emailAcquisition, ...sendMyBasket, ...displayOnly, ...mpsEmbed, ...mpsPopup];
      }

      return templates.filter(template => template.isArchived === true);
    }

    // For email and SMS, use the archived templates endpoint
    const queryParams = new URLSearchParams({ channel });
    if (templateType) {
      const backendTemplateType = TAB_TO_TEMPLATE_TYPE[templateType] || templateType;
      queryParams.append('templateType', backendTemplateType);
    }
    const result = await get<ITemplate[]>(
      `${generatePath(TemplateApiEndpoints.GET_ARCHIVED_TEMPLATES, { siteId })}?${queryParams.toString()}`,
    );
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getUnsubscribeTemplates = async ({ siteId, t }: TemplatesRequest): Promise<ITemplate[]> => {
  try {
    const result = await get<ITemplate[]>(generatePath(TemplateApiEndpoints.GET_UNSUBSCRIBE_TEMPLATES, { siteId }));
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getGlobalUnsubscribeTemplates = async ({
  siteId,
  t,
  accessLevel,
}: TemplatesRequest): Promise<ITemplate[]> => {
  if (accessLevel !== 'SaleCycle') {
    return [];
  }

  siteId = import.meta.env.VITE_GLOBAL_SITE_ID;
  try {
    const result = await get<ITemplate[]>(
      generatePath(TemplateApiEndpoints.GET_GLOBAL_UNSUBSCRIBE_TEMPLATES, { siteId }),
    );
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getEmailTrackingUnsubscribeSiteTemplates = async ({
  siteId,
  t,
}: TemplatesRequest): Promise<ITemplate[]> => {
  try {
    const result = await get<ITemplate[]>(
      generatePath(TemplateApiEndpoints.GET_EMAIL_TRACKING_UNSUBSCRIBE_TEMPLATES, { siteId }),
    );
    return toTemplateArray(result?.data);
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getGlobalEmailTrackingUnsubscribeTemplates = async ({
  siteId,
  t,
  accessLevel,
}: TemplatesRequest): Promise<ITemplate[]> => {
  if (accessLevel !== 'SaleCycle') {
    return [];
  }

  siteId = import.meta.env.VITE_GLOBAL_SITE_ID;
  try {
    const result = await get<ITemplate[]>(
      generatePath(TemplateApiEndpoints.GET_GLOBAL_EMAIL_TRACKING_UNSUBSCRIBE_TEMPLATES, { siteId }),
    );
    return toTemplateArray(result?.data);
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getEmailAcquisitions = async ({ siteId, t }: TemplatesRequest): Promise<ITemplate[]> => {
  try {
    const result = await get<ITemplate[]>(
      generatePath(TemplateApiEndpoints.GET_EMAIL_ACQUISITION_TEMPLATES, { siteId }),
    );
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getSendMyBasketOnsiteTemplates = async ({ siteId, t }: TemplatesRequest): Promise<ITemplate[]> => {
  try {
    const result = await get<ITemplate[]>(generatePath(TemplateApiEndpoints.GET_SEND_MY_BASKET_TEMPLATES, { siteId }));
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getDisplayOnlyTemplates = async ({ siteId, t }: TemplatesRequest): Promise<ITemplate[]> => {
  try {
    const result = await get<ITemplate[]>(generatePath(TemplateApiEndpoints.GET_DISPLAY_ONLY_TEMPLATES, { siteId }));
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getMPSEmbedTemplates = async ({ siteId, t }: TemplatesRequest): Promise<ITemplate[]> => {
  try {
    const result = await get<ITemplate[]>(generatePath(TemplateApiEndpoints.GET_MPS_EMBED_TEMPLATES, { siteId }));
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export const getMPSPopupTemplates = async ({ siteId, t }: TemplatesRequest): Promise<ITemplate[]> => {
  try {
    const result = await get<ITemplate[]>(generatePath(TemplateApiEndpoints.GET_MPS_POPUP_TEMPLATES, { siteId }));
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.getTemplates.error'));
  }
};

export interface CreateTemplateLanguage {
  siteId: string;
  templateId: string;
  language: string;
  template: string;
  comment: string;
  type: string;
  t: TFunction;
}

export const addOffsiteTemplateLanguage = async ({
  siteId,
  templateId,
  language,
  template,
  comment,
  type,
  t,
}: CreateTemplateLanguage): Promise<ITemplate> => {
  try {
    const result = await put<ITemplate, IAddTemplateLanguagePayload>(
      generatePath(TemplateApiEndpoints.ADD_TEMPLATE_LANGUAGE, { siteId, templateId, language }),
      {
        template,
        comment,
        type,
      },
    );
    if (result?.data === null) {
      throw Error(t('designs:designGallery.templateList.queries.addTemplateLanguage.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.addTemplateLanguage.error'));
  }
};

export const addOnsiteTemplateLanguage = async ({
  siteId,
  templateId,
  language,
  template,
  comment,
  type,
  t,
}: CreateTemplateLanguage): Promise<ITemplate> => {
  try {
    const result = await put<ITemplate, IAddTemplateLanguagePayload>(
      generatePath(TemplateApiEndpoints.ADD_ONSITE_TEMPLATE_LANGUAGE, { siteId, templateId, language }),
      {
        template,
        comment,
        type,
      },
    );
    if (result?.data === null) {
      throw Error(t('designs:designGallery.templateList.queries.addTemplateLanguage.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.addTemplateLanguage.error'));
  }
};

interface UpdateTemplateContainer {
  siteId: string;
  templateId: string;
  enrichmentConfigIds: string[];
  templateName: string;
  type: string;
  channel: string;
  templateLanguages: ITemplateLanguageStatus[];
  isEmbedded?: boolean;
  t: TFunction;
}

export const updateTemplateContainer = async ({
  siteId,
  templateId,
  enrichmentConfigIds,
  templateName,
  type,
  channel,
  templateLanguages,
  isEmbedded,
  t,
}: UpdateTemplateContainer): Promise<TemplateContainer> => {
  try {
    const basePath =
      channel === 'onsite'
        ? TemplateApiEndpoints.UPDATE_ONSITE_TEMPLATE_CONTAINER
        : TemplateApiEndpoints.UPDATE_TEMPLATE_CONTAINER;

    const body = {
      enrichmentConfigIds,
      templateName,
      type,
      channel,
    };

    if (channel === 'onsite') {
      body['onsiteTemplateLanguages'] = templateLanguages;
      body['isEmbedded'] = isEmbedded;
    } else {
      body['templateLanguages'] = templateLanguages;
    }

    const result = await put<TemplateContainer, TemplateContainer | OnsiteTemplateContainer>(
      generatePath(basePath, { siteId, templateId }),
      body,
    );
    if (result?.data === null) {
      throw Error(t('designs:designGallery.templateList.queries.updateContainer.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.updateContainer.error'));
  }
};

interface SubjectLinesRequest {
  siteId: string;
  t: TFunction;
}

export const getSubjectLines = async ({ siteId, t }: SubjectLinesRequest): Promise<ISubjectLine[]> => {
  try {
    const result = await get<ISubjectLine[]>(generatePath(TemplateApiEndpoints.GET_SUBJECT_LINES, { siteId }));
    if (result?.data === null) {
      return [];
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.subjectLines.queries.getSubjectLines.error'));
  }
};

export const cloneTemplate = async ({ siteId, templateId, type, t }): Promise<ITemplate[]> => {
  try {
    const result = await post<ITemplate[], any>(
      generatePath(TemplateApiEndpoints.CLONE_TEMPLATE, { siteId, templateId }),
      { type },
    );
    if (result?.data === null) {
      throw Error(t('designs:designGallery.templateList.queries.updateContainer.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.updateContainer.error'));
  }
};

export const cloneOnsiteTemplate = async ({ siteId, templateId, type, t }): Promise<ITemplate[]> => {
  try {
    const result = await post<ITemplate[], any>(
      generatePath(TemplateApiEndpoints.CLONE_ONSITE_TEMPLATE, { siteId, templateId }),
      { type },
    );
    if (result?.data === null) {
      throw Error(t('designs:designGallery.templateList.queries.updateContainer.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.updateContainer.error'));
  }
};

const _getTemplateCampaignType = (channel: string, campaignType: string) => {
  return channel === 'onsite'
    ? 'onsite'
    : campaignType === TemplateTypes.sendMyBasket ||
      campaignType === 'emailAcquisition' ||
      campaignType === 'dataAcquisition' // for some reason the backend is returning both 'emailAcquisition' and 'dataAcquisition' for data acquisition templates
    ? 'onsite'
    : 'offsite';
};

export const checkTemplateUsage = async ({
  siteId,
  templateId,
  channel,
  campaignType,
  t,
}: {
  siteId: string;
  templateId: string;
  channel?: string;
  campaignType?: string;
  t: TFunction;
}): Promise<{ isInUse: boolean }> => {
  try {
    // For onsite templates (including MPS), use the onsite endpoint
    // The backend will automatically detect MPS templates and call the correct campaign API
    if (channel === 'onsite') {
      const result = await get<{ isInUse: boolean }>(
        generatePath(TemplateApiEndpoints.CHECK_ONSITE_TEMPLATE_USAGE, {
          siteId,
          templateId,
        }),
      );
      if (result?.data === null) {
        throw Error(t('designs:designGallery.templateList.queries.checkTemplateUsage.error'));
      }
      return result.data;
    }

    // For offsite templates, use the offsite endpoint
    const result = await get<{ isInUse: boolean }>(
      generatePath(TemplateApiEndpoints.CHECK_TEMPLATE_USAGE, {
        siteId,
        templateId,
        campaignType: _getTemplateCampaignType(channel, campaignType),
      }),
    );
    if (result?.data === null) {
      throw Error(t('designs:designGallery.templateList.queries.checkTemplateUsage.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.checkTemplateUsage.error'));
  }
};

export const archiveTemplate = async ({
  siteId,
  templateId,
  channel,
  campaignType,
  t,
}: {
  siteId: string;
  templateId: string;
  channel?: string;
  campaignType?: string;
  t: TFunction;
}): Promise<void> => {
  try {
    const endpoint =
      channel === 'onsite' ? TemplateApiEndpoints.ARCHIVE_ONSITE_TEMPLATE : TemplateApiEndpoints.ARCHIVE_TEMPLATE;

    const pathParams =
      channel === 'onsite'
        ? { siteId, templateId }
        : {
            siteId,
            templateId,
            campaignType: _getTemplateCampaignType(channel, campaignType),
          };

    await put<void>(generatePath(endpoint, pathParams));
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.archiveTemplate.error'));
  }
};

// This will need to be revisited after backend implementation. Just placeholder guesswork for now.
export const restoreTemplate = async ({
  siteId,
  templateId,
  channel,
  t,
}: {
  siteId: string;
  templateId: string;
  channel?: string;
  t: TFunction;
}): Promise<void> => {
  try {
    const endpoint =
      channel === 'onsite' ? TemplateApiEndpoints.RESTORE_ONSITE_TEMPLATE : TemplateApiEndpoints.RESTORE_TEMPLATE;
    await put<void>(generatePath(endpoint, { siteId, templateId }));
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.restoreTemplate.error'));
  }
};

export const deleteTemplate = async ({
  siteId,
  templateId,
  channel,
  campaignType,
  t,
}: {
  siteId: string;
  templateId: string;
  channel?: string;
  campaignType?: string;
  t: TFunction;
}): Promise<void> => {
  try {
    const endpoint =
      channel === 'onsite' ? TemplateApiEndpoints.DELETE_ONSITE_TEMPLATE : TemplateApiEndpoints.DELETE_TEMPLATE;

    if (channel === 'onsite') {
      await del<void>(generatePath(endpoint, { siteId, templateId }));
    } else {
      const resolvedCampaignType = _getTemplateCampaignType(channel, campaignType);
      const path = generatePath(endpoint, { siteId, templateId });
      const url = `${path}?campaignType=${resolvedCampaignType}`;
      await del<void>(url);
    }
  } catch (e) {
    throw Error(t('designs:designGallery.templateList.queries.deleteTemplate.error'));
  }
};

interface CreateSubjectLineRequest {
  siteId: string;
  enabled: boolean;
  subjectLines: Record<string, string>;
  audit?: Audit;
  t: TFunction;
}

interface UpdateSubjectLineRequest {
  id: string;
  siteId: string;
  enabled: boolean;
  subjectLines: Record<string, string>;
  audit: Audit;
  t: TFunction;
}

interface Audit {
  lastUpdatedOn?: number;
  lastUpdatedBy?: string;
  comment?: string;
}

interface ISubjectLinePayload {
  enabled: boolean;
  subjectLines: Record<string, string>;
  audit?: Audit;
}

export const createSubjectLine = async ({
  siteId,
  enabled,
  subjectLines,
  audit,
  t,
}: CreateSubjectLineRequest): Promise<ISubjectLine> => {
  try {
    const result = await post<ISubjectLine, ISubjectLinePayload>(
      generatePath(TemplateApiEndpoints.ADD_SUBJECT_LINE, { siteId }),
      {
        enabled,
        subjectLines,
        audit,
      },
    );

    return result?.data ?? null;
  } catch (e) {
    throw Error(t('designs:designGallery.subjectLines.queries.createSubjectLine.error'));
  }
};

export const updateSubjectLine = async ({
  id,
  siteId,
  enabled,
  subjectLines,
  audit,
  t,
}: UpdateSubjectLineRequest): Promise<ISubjectLine> => {
  try {
    const result = await put<ISubjectLine, ISubjectLinePayload>(
      generatePath(TemplateApiEndpoints.UPDATE_SUBJECT_LINE, {
        siteId,
        subjectLineId: id,
      }),
      {
        enabled,
        subjectLines,
        audit,
      },
    );

    return result?.data ?? null;
  } catch (e) {
    throw Error(t('designs:designGallery.subjectLines.queries.updateSubjectLine.error'));
  }
};
